import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ProjectionSet, ProjectionScenario, ProjectionFieldValue, ProjectionAppliedStrategy, ProjectionComputedResults, ProjectionStrategyLine } from '../../../models/projection.model';
import {
  PROJECTION_SECTIONS,
  PROJECTION_FIELDS,
  ProjectionSectionDef,
  ProjectionFieldDef,
  getFieldsForSection
} from '../../../constants/projection-field-definitions';
import { TaxCalcService } from '../../../services/tax-calc.service';
import { TaxProjectionsService } from '../../../tpiq-services/tax-projections.service';
import { TaxPlanningService } from '../../../tpiq-services/tax-planning.service';
import { ToastService } from '../../../services/Toast.service';
import { TOAST_TYPE } from '../../../constants/Tpiq.constants';
import { Session } from '../../../services/Session';
import { ProjectionScenarioHeaderComponent } from './projection-scenario-header.component';
import { ProjectionFieldCellComponent } from './projection-field-cell.component';

@Component({
  selector: 'app-projection-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ProjectionScenarioHeaderComponent,
    ProjectionFieldCellComponent
  ],
  templateUrl: './projection-grid.component.html',
  styleUrl: './projection-grid.component.scss'
})
export class ProjectionGridComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectionSet: ProjectionSet;
  @Input() scenarios: ProjectionScenario[] = [];
  @Input() recalcTrigger: number = 0;
  @Output() addScenarioEvent = new EventEmitter<void>();
  @Output() deleteScenarioEvent = new EventEmitter<string>();
  @Output() filingStatusChanged = new EventEmitter<string>();

  sections: ProjectionSectionDef[] = PROJECTION_SECTIONS;
  allFields: ProjectionFieldDef[] = PROJECTION_FIELDS;

  // Section collapse state
  collapsedSections: { [sectionKey: string]: boolean } = {};

  // Field values indexed by scenarioId -> fieldKey -> value
  fieldValueMap: { [scenarioId: string]: { [fieldKey: string]: number } } = {};

  // Apply Strategies modal state
  showApplyStrategiesModal: boolean = false;
  applyStrategyScenarioId: string = '';
  clientPlans: any[] = [];
  selectedPlanId: string = '';
  applyingStrategies: boolean = false;

  // Strategy selection
  strategyLines: ProjectionStrategyLine[] = [];
  selectedLineIds: Set<string> = new Set();
  loadingStrategies: boolean = false;
  selectAll: boolean = true;
  strategySearchTerm: string = '';

  // Dirty fields per scenario for debounced auto-save
  private dirtyFields: { [scenarioId: string]: Set<string> } = {};
  private saveTimers: { [scenarioId: string]: any } = {};

  constructor(
    private taxCalcService: TaxCalcService,
    private taxProjectionsService: TaxProjectionsService,
    private taxPlanningService: TaxPlanningService,
    private toastService: ToastService
  ) {}

  // ─── Lifecycle ────────────────────────────────────────────────────

  ngOnInit(): void {
    this.buildFieldValueMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scenarios) {
      this.buildFieldValueMap();
      this.recalculateAllScenarios();
    }
    if (changes.recalcTrigger && !changes.recalcTrigger.firstChange) {
      this.recalculateAllScenarios();
    }
  }

  ngOnDestroy(): void {
    for (const scenarioId of Object.keys(this.saveTimers)) {
      if (this.saveTimers[scenarioId]) {
        clearTimeout(this.saveTimers[scenarioId]);
        this.flushDirtyFields(scenarioId);
      }
    }
  }

  // ─── Field Value Map ──────────────────────────────────────────────

  buildFieldValueMap(): void {
    this.fieldValueMap = {};
    if (!this.scenarios) return;

    for (const scenario of this.scenarios) {
      this.fieldValueMap[scenario.id] = {};
      if (scenario.fieldValues) {
        for (const fv of scenario.fieldValues) {
          this.fieldValueMap[scenario.id][fv.fieldKey] = fv.amount;
        }
      }
    }
  }

  getFieldValue(scenarioId: string, fieldKey: string): number {
    if (this.fieldValueMap[scenarioId]) {
      return this.fieldValueMap[scenarioId][fieldKey] || 0;
    }
    return 0;
  }

  getDropdownValue(scenarioId: string, fieldKey: string): string {
    const val = this.getFieldValue(scenarioId, fieldKey);
    return val ? String(val) : '';
  }

  getFieldsForSection(sectionKey: string): ProjectionFieldDef[] {
    return getFieldsForSection(sectionKey);
  }

  // ─── Section Visibility ───────────────────────────────────────────

  isSectionVisible(section: ProjectionSectionDef): boolean {
    if (section.key === 'spouse') {
      const fs = this.projectionSet?.filingStatus || '';
      return fs.includes('Married') || fs.includes('MFJ') || fs.includes('MFS');
    }
    if (section.type === 'sub' && section.parentSection) {
      return !this.collapsedSections[section.parentSection];
    }
    return true;
  }

  isFieldVisible(field: ProjectionFieldDef): boolean {
    if (field.section === 'spouse') {
      const fs = this.projectionSet?.filingStatus || '';
      if (!(fs.includes('Married') || fs.includes('MFJ') || fs.includes('MFS'))) {
        return false;
      }
    }
    const sectionDef = this.sections.find(s => s.key === field.section);
    if (!sectionDef) return true;
    if (sectionDef.type === 'sub' && sectionDef.parentSection) {
      return !this.collapsedSections[sectionDef.parentSection] && !this.collapsedSections[field.section];
    }
    return !this.collapsedSections[field.section];
  }

  toggleSection(sectionKey: string): void {
    this.collapsedSections[sectionKey] = !this.collapsedSections[sectionKey];
  }

  // ─── Field Value Changes & Auto-Save ──────────────────────────────

  onFieldValueChange(scenarioId: string, fieldKey: string, value: any): void {
    if (!this.fieldValueMap[scenarioId]) {
      this.fieldValueMap[scenarioId] = {};
    }

    if (fieldKey === 'filing_status') {
      this.onFilingStatusChange(value);
      return;
    }

    this.fieldValueMap[scenarioId][fieldKey] = parseFloat(value) || 0;
    this.recalculateSubtotals(scenarioId);

    if (!this.dirtyFields[scenarioId]) {
      this.dirtyFields[scenarioId] = new Set();
    }
    this.dirtyFields[scenarioId].add(fieldKey);
    this.debouncedSave(scenarioId);
  }

  private onFilingStatusChange(value: string): void {
    if (this.projectionSet) {
      this.projectionSet.filingStatus = value;
      this.filingStatusChanged.emit(value);
      for (const scenario of this.scenarios) {
        this.recalculateSubtotals(scenario.id);
      }
    }
  }

  private debouncedSave(scenarioId: string): void {
    if (this.saveTimers[scenarioId]) {
      clearTimeout(this.saveTimers[scenarioId]);
    }
    this.saveTimers[scenarioId] = setTimeout(() => {
      this.flushDirtyFields(scenarioId);
    }, 500);
  }

  private flushDirtyFields(scenarioId: string): void {
    const dirty = this.dirtyFields[scenarioId];
    if (!dirty || dirty.size === 0) return;

    const org = Session.getOrg();
    const userId = Session.getUser()?.id;
    if (!org || !userId || !this.projectionSet) return;

    const values: ProjectionFieldValue[] = [];
    for (const fieldKey of dirty) {
      values.push({
        scenarioId: scenarioId,
        fieldKey: fieldKey,
        amount: this.fieldValueMap[scenarioId][fieldKey] || 0,
        isOverride: false
      });
    }

    this.taxProjectionsService.bulkUpsertFieldValues(
      org, userId, this.projectionSet.clientId, this.projectionSet.id, scenarioId, values
    ).subscribe(
      () => {},
      error => console.error('Auto-save failed for scenario ' + scenarioId, error)
    );

    this.dirtyFields[scenarioId] = new Set();
    delete this.saveTimers[scenarioId];
  }

  // ─── Tax Recalculation ────────────────────────────────────────────

  recalculateAllScenarios(): void {
    for (const scenario of this.scenarios) {
      this.recalculateSubtotals(scenario.id);
    }
  }

  recalculateSubtotals(scenarioId: string): void {
    const subtotalFields = this.allFields.filter(f => f.isSubtotal && f.computeFrom);
    for (const subtotal of subtotalFields) {
      let sum = 0;
      for (const childKey of subtotal.computeFrom) {
        sum += this.getFieldValue(scenarioId, childKey);
      }
      this.fieldValueMap[scenarioId][subtotal.key] = sum;
    }

    const filingStatus = this.projectionSet?.filingStatus || 'Single';
    const homeState = this.projectionSet?.homeState || '';
    const projectionYear = this.projectionSet?.projectionYear || new Date().getFullYear();

    const results: ProjectionComputedResults = this.taxCalcService.computeAll(
      this.fieldValueMap[scenarioId],
      filingStatus,
      homeState,
      projectionYear
    );

    for (const [key, value] of Object.entries(results.sectionSubtotals)) {
      this.fieldValueMap[scenarioId][key] = value;
    }

    const scenario = this.scenarios.find(s => s.id === scenarioId);
    if (scenario) {
      scenario.taxableIncome = results.taxableIncome;
      scenario.totalTax = results.totalTax;
    }
  }

  // ─── Scenario Actions ─────────────────────────────────────────────

  onAddScenario(): void {
    this.addScenarioEvent.emit();
  }

  onDeleteScenario(scenarioId: string): void {
    this.deleteScenarioEvent.emit(scenarioId);
  }

  getScenarioYear(scenario: ProjectionScenario): number {
    if (scenario.type === 'previous_return') {
      return this.projectionSet?.sourceOcrYear
        || (this.projectionSet?.projectionYear || new Date().getFullYear()) - 1;
    }
    return this.projectionSet?.projectionYear || new Date().getFullYear();
  }

  // ─── Baseline Comparison ──────────────────────────────────────────

  private getBaselineScenario(): ProjectionScenario | null {
    return this.scenarios.find(s => s.type === 'baseline') || null;
  }

  getBaselineDelta(scenario: ProjectionScenario): number | null {
    if (scenario.type === 'baseline' || scenario.type === 'previous_return') return null;
    const baseline = this.getBaselineScenario();
    if (!baseline || !baseline.totalTax) return null;
    return (scenario.totalTax || 0) - baseline.totalTax;
  }

  // ─── Apply Strategies Modal ───────────────────────────────────────

  get activePlanName(): string {
    const plan = this.clientPlans.find(p => (p.clientStrategyId || p.id) === this.selectedPlanId);
    return plan ? plan.name : '';
  }

  openApplyStrategies(scenarioId: string): void {
    this.applyStrategyScenarioId = scenarioId;
    this.selectedPlanId = '';
    this.clientPlans = [];
    this.strategyLines = [];
    this.selectedLineIds = new Set();
    this.loadingStrategies = true;
    this.showApplyStrategiesModal = true;

    const org = Session.getOrg();
    const userId = Session.getUser()?.id;
    if (!org || !userId || !this.projectionSet) return;

    // Load plans then immediately load strategy lines for the active plan
    this.taxPlanningService.getPlansForClient(org, userId, this.projectionSet.clientId)
      .subscribe(
        response => {
          this.clientPlans = response || [];
          if (this.clientPlans.length === 0) {
            this.loadingStrategies = false;
            return;
          }
          // Auto-select the first plan (user is already in plan context)
          this.selectedPlanId = this.clientPlans[0].clientStrategyId || this.clientPlans[0].id;
          this.loadStrategyLines();
        },
        error => {
          this.loadingStrategies = false;
          this.toastService.pop(TOAST_TYPE.error, 'Failed to load plans');
        }
      );
  }

  private loadStrategyLines(): void {
    const org = Session.getOrg();
    const userId = Session.getUser()?.id;
    if (!org || !userId || !this.projectionSet || !this.selectedPlanId) return;

    this.loadingStrategies = true;
    this.taxProjectionsService.getStrategyLinesForPlan(
      org, userId, this.projectionSet.clientId, this.projectionSet.id, this.selectedPlanId
    ).subscribe(
      (lines: ProjectionStrategyLine[]) => {
        // Sort: AI recommended first, then in-plan, then rest alphabetically
        lines.sort((a, b) => {
          if (a.aiRecommended !== b.aiRecommended) return a.aiRecommended ? -1 : 1;
          if (a.inPlan !== b.inPlan) return a.inPlan ? -1 : 1;
          if (a.inPlan && b.inPlan) return (b.totalSavings || 0) - (a.totalSavings || 0);
          return (a.strategyName || '').localeCompare(b.strategyName || '');
        });
        this.strategyLines = lines;
        // Pre-select only strategies that are in the plan
        this.selectedLineIds = new Set(lines.filter(l => l.inPlan).map(l => l.id));
        this.selectAll = this.selectedLineIds.size === lines.length;
        this.loadingStrategies = false;
      },
      error => {
        this.loadingStrategies = false;
        this.toastService.pop(TOAST_TYPE.error, 'Failed to load strategies');
      }
    );
  }

  toggleStrategyLine(lineId: string): void {
    if (this.selectedLineIds.has(lineId)) {
      this.selectedLineIds.delete(lineId);
    } else {
      this.selectedLineIds.add(lineId);
    }
    this.selectAll = this.selectedLineIds.size === this.strategyLines.length;
  }

  toggleSelectAll(): void {
    if (this.selectAll) {
      this.selectedLineIds = new Set();
      this.selectAll = false;
    } else {
      this.selectedLineIds = new Set(this.strategyLines.map(l => l.id));
      this.selectAll = true;
    }
  }

  isLineSelected(lineId: string): boolean {
    return this.selectedLineIds.has(lineId);
  }

  getSelectedSavingsTotal(): number {
    return this.strategyLines
      .filter(l => this.selectedLineIds.has(l.id))
      .reduce((sum, l) => sum + (l.totalSavings || 0), 0);
  }

  get filteredStrategyLines(): ProjectionStrategyLine[] {
    if (!this.strategySearchTerm) return this.strategyLines;
    const term = this.strategySearchTerm.toLowerCase();
    return this.strategyLines.filter(l => l.strategyName.toLowerCase().includes(term));
  }

  formatModalCurrency(value: number): string {
    if (value == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  closeApplyStrategiesModal(): void {
    this.showApplyStrategiesModal = false;
    this.applyStrategyScenarioId = '';
    this.selectedPlanId = '';
    this.strategyLines = [];
    this.selectedLineIds = new Set();
    this.strategySearchTerm = '';
  }

  applySelectedStrategies(): void {
    if (!this.selectedPlanId || !this.applyStrategyScenarioId || this.selectedLineIds.size === 0) return;

    const org = Session.getOrg();
    const userId = Session.getUser()?.id;
    if (!org || !userId || !this.projectionSet) return;

    const lineIds = Array.from(this.selectedLineIds);

    this.applyingStrategies = true;
    this.taxProjectionsService.applyStrategies(
      org, userId, this.projectionSet.clientId, this.projectionSet.id,
      this.applyStrategyScenarioId, this.selectedPlanId, lineIds
    ).subscribe(
      (adjustments: ProjectionAppliedStrategy[]) => {
        this.applyingStrategies = false;
        this.closeApplyStrategiesModal();

        const scenario = this.scenarios.find(s => s.id === this.applyStrategyScenarioId);
        if (scenario) {
          scenario.appliedStrategies = adjustments;
          scenario.appliedPlanId = this.selectedPlanId;
        }

        this.toastService.pop(TOAST_TYPE.success, adjustments.length + ' strategies applied successfully');
      },
      error => {
        this.applyingStrategies = false;
        this.toastService.pop(TOAST_TYPE.error, 'Failed to apply strategies');
      }
    );
  }

  clearAppliedStrategies(scenarioId: string): void {
    const org = Session.getOrg();
    const userId = Session.getUser()?.id;
    if (!org || !userId || !this.projectionSet) return;

    this.taxProjectionsService.clearStrategies(
      org, userId, this.projectionSet.clientId, this.projectionSet.id, scenarioId
    ).subscribe(
      () => {
        const scenario = this.scenarios.find(s => s.id === scenarioId);
        if (scenario) {
          scenario.appliedStrategies = [];
          scenario.appliedPlanId = null;
        }
        this.toastService.pop(TOAST_TYPE.success, 'Strategies cleared');
      },
      error => {
        this.toastService.pop(TOAST_TYPE.error, 'Failed to clear strategies');
      }
    );
  }
}
